import { Box, Container, Fab, Button } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import {
  GoalActionStatusWithAction,
  GoalWithObjective,
} from "components/types";
import { ActionStatus, Goal, CampaignStepType } from "database";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PATH_DASHBOARD } from "routes/paths";
import useAsyncEffect from "use-async-effect";
import axios from "../../utils/axios";
import { GoalDetailsCard } from "./GoalDetailsCard";
import { useCampaigns } from "components/Campaigns/useCampaigns";

import { useLocales } from "../../locales";
import { useTheme } from "@mui/material/styles";

export const GoalTrackPage = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { translate } = useLocales();
  const navigate = useNavigate();
  const { data: campaigns } = useCampaigns();
  const [goal, setGoal] = React.useState<GoalWithObjective>();
  const [actionStatuses, setActionStatuses] =
    React.useState<GoalActionStatusWithAction[]>();
  const [isGoalStepActive, setIsGoalStepActive] = useState(false);

  useAsyncEffect(async () => {
    const { data } = await axios.get<GoalWithObjective>(`/goals/find/${id}`);

    const { data: actions } = await axios.get<GoalActionStatusWithAction[]>(
      `/goals/actionStatuses/${id}`
    );
    setActionStatuses(actions);
    setGoal(data);
  }, []);

  // Check if Goals campaign step is active
  useEffect(() => {
    if (campaigns && campaigns.length > 0) {
      const lastCampaign = campaigns[campaigns.length - 1];
      const currentDate = new Date();
      const goalStep = lastCampaign?.CampaignStep.find(
        (cs) => cs.type === CampaignStepType.GOAL
      );
      const goalStartDate = goalStep?.startDate || new Date(lastCampaign.startDate);
      const goalEndDate = goalStep?.endDate || new Date(lastCampaign.startDate);

      const goalStepActive =
        currentDate >= new Date(goalStartDate) &&
        currentDate <= new Date(goalEndDate);
      
      setIsGoalStepActive(goalStepActive);
    }
  }, [campaigns]);

  const handleEditGoal = () => {
    // Navigate to goal builder with the goal ID to prefill data
    const editUrl = `/goals/builder/edit/${id}`;
    navigate(editUrl);
  };

  if (!goal) {
    return null;
  }

  // Transform the data to match GoalDetailsCard format
  const goalData = {
    id: goal.id,
    targetRating: goal.targetRating || 0,
    targetDate: goal.targetDate || new Date(),
    dateCompleted: goal.dateCompleted,
    goalObjective: {
      id: goal.GoalObjective?.id || '',
      title: goal.GoalObjective?.text || '',
      text: goal.GoalObjective?.text || '',
      textFR: goal.GoalObjective?.textFR || '',
      category: goal.GoalObjective?.category || '',
    },
    goalActionStatus: actionStatuses?.map(actionStatus => ({
      id: actionStatus.id,
      isDone: actionStatus.status === ActionStatus.DONE,
      GoalAction: {
        id: actionStatus.GoalAction.id,
        title: actionStatus.GoalAction.text,
      },
    })) || [],
  };

  return (
    <Container maxWidth="lg">
      {/* Goal Details Card - Reusing existing component */}
      <GoalDetailsCard goalData={goalData} isHRView={true} />
      
      {/* Action Buttons - Below the Goal card */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
        {/* Edit Button - Only show when goal step is active and goal is not completed */}
        {isGoalStepActive && !goal.dateCompleted && (
          <Button
            variant="outlined"
            startIcon={<Edit />}
                onClick={handleEditGoal}
            sx={{
              borderColor: theme.palette.secondary.main,
              color: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.light + '20',
                borderColor: theme.palette.secondary.dark,
              },
            }}
          >
            {translate("goals_page.edit") as unknown as string}
          </Button>
        )}
        
        {/* Close Goal Button - Only show when goal is not completed */}
        {!goal.dateCompleted && (
          <Fab
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
              boxShadow: theme.shadows[4],
            }}
            variant="extended"
                onClick={async () => {
                  try {
                    const { data } = await axios.patch<Goal>(`/goals/${id}`, {
                      dateCompleted: new Date().toISOString()
                    });
                    navigate(PATH_DASHBOARD.general.goals);
                  } catch (error: any) {
                    console.error('Failed to close goal:', error);
                  }
                }}
          >
            <Add />
            {translate("goals_page.close_goal") as unknown as string}
          </Fab>
        )}
      </Box>
    </Container>
  );
};

